import { computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthService } from '@services/auth.service';
import { Entrada } from '@shared/interfaces/entrada';
import { QuantidadesMenu } from '@shared/interfaces/quantidadesMenu';
import { EMPTY, distinctUntilChanged, filter, finalize, map, pipe, startWith, switchMap } from 'rxjs';
import { MENU_INICIAL_BASE, MENU_PRONTUARIO_BASE, MenuInicialBaseItem } from '../../core/configs/menu.config';
import { ProntuarioService } from '../../core/services/prontuario/prontuario.service';

export interface NavegacaoMenuItem {
  label?: string;
  icon?: string;
  routerLink?: string[];
  routerLinkActiveOptions?: object;
  items?: NavegacaoMenuItem[];
  command?: (event: { originalEvent: Event; item: NavegacaoMenuItem }) => void;
  disabled?: boolean;
  tooltip?: string;
  visible?: boolean;
  class?: string;
  badge?: string;
  separator?: boolean;
}

export function rastrearBlocoMenu(indice: number, item: NavegacaoMenuItem): string {
  if (item.separator === true) {
    return `separador-${indice}`;
  }
  return item.label ?? `bloco-${indice}`;
}

export function rastrearItemMenu(indice: number, item: NavegacaoMenuItem): string {
  const chaveRota = item.routerLink?.join('/') ?? '';
  return chaveRota.length > 0 ? chaveRota : (item.label ?? `item-${indice}`);
}

interface NavegacaoState {
  contexto: 'inicial' | 'prontuario';
  entradaId: number | null;
  entrada: Entrada | null;
  carregandoEntrada: boolean;
  quantidades: Partial<QuantidadesMenu>;
  personalizados: string[];
  termoBuscaMenu: string;
}

const estadoInicial: NavegacaoState = {
  contexto: 'inicial',
  entradaId: null,
  entrada: null,
  carregandoEntrada: false,
  quantidades: JSON.parse(localStorage.getItem('pep-quantidades-menu') ?? '{}'),
  personalizados: localStorage.getItem('personalizados')?.split(',').filter(Boolean) ?? [],
  termoBuscaMenu: '',
};

function normalizarTextoBusca(texto: string): string {
  return texto.normalize('NFD').replaceAll(/\p{M}/gu, '').toLowerCase().trim();
}

function rotuloCorrespondeAoTermo(label: string | undefined, termoNormalizado: string): boolean {
  if (!label) {
    return false;
  }
  return normalizarTextoBusca(label).includes(termoNormalizado);
}

function filtrarItemMenu(item: NavegacaoMenuItem, termoNormalizado: string): NavegacaoMenuItem | null {
  if (item.separator) {
    return null;
  }

  if (item.items && item.items.length > 0) {
    const filhosFiltrados = item.items
      .map((filho) => filtrarItemMenu(filho, termoNormalizado))
      .filter((filho): filho is NavegacaoMenuItem => filho !== null);

    if (rotuloCorrespondeAoTermo(item.label, termoNormalizado)) {
      return { ...item, items: item.items };
    }
    if (filhosFiltrados.length > 0) {
      return { ...item, items: filhosFiltrados };
    }
    return null;
  }

  return rotuloCorrespondeAoTermo(item.label, termoNormalizado) ? item : null;
}

function filtrarMenuPorTermo(itens: NavegacaoMenuItem[], termoNormalizado: string): NavegacaoMenuItem[] {
  if (!termoNormalizado) {
    return itens;
  }

  const resultado: NavegacaoMenuItem[] = [];
  for (const item of itens) {
    if (item.separator) {
      continue;
    }
    const filtrado = filtrarItemMenu(item, termoNormalizado);
    if (filtrado) {
      resultado.push(filtrado);
    }
  }
  return resultado;
}

function buildMenuInicialItem(item: MenuInicialBaseItem, authService: AuthService): NavegacaoMenuItem {
  const semPermissao = item.role ? !authService.hasRole(item.role) : false;
  return {
    label: item.label,
    icon: item.icon,
    routerLink: item.path ? [item.path] : undefined,
    routerLinkActiveOptions: { exact: true },
    items: item.children?.map((c) => buildMenuInicialItem(c, authService)),
    disabled: semPermissao,
    tooltip: semPermissao ? 'Sem permissão para acessar este menu' : undefined,
    visible: true,
  };
}

function construirMenuInicial(authService: AuthService): NavegacaoMenuItem[] {
  const porCaminho = (path: string): NavegacaoMenuItem | undefined => {
    const item = MENU_INICIAL_BASE.find((i) => i.path === path);
    return item ? buildMenuInicialItem(item, authService) : undefined;
  };

  const porLabel = (label: string): NavegacaoMenuItem | undefined => {
    const item = MENU_INICIAL_BASE.find((i) => i.label === label);
    return item ? buildMenuInicialItem(item, authService) : undefined;
  };

  const compactar = (itens: (NavegacaoMenuItem | undefined)[]): NavegacaoMenuItem[] =>
    itens.filter((i): i is NavegacaoMenuItem => i !== undefined);

  return [
    {
      label: 'INÍCIO',
      items: compactar([porCaminho('/'), porCaminho('pacientes')]),
    },
    { separator: true },
    {
      label: 'ATENDIMENTOS',
      items: compactar([
        porCaminho('classificacoes-risco'),
        porCaminho('entradas-pnrf'),
        porCaminho('prescricoes-medicas'),
        porCaminho('boletins-medicos'),
        porCaminho('acompanhamentos'),
        porCaminho('mapa-visita'),
      ]),
    },
    { separator: true },
    {
      label: 'SOLICITAÇÕES',
      items: compactar([
        porCaminho('solicitacoes-antimicrobianos'),
        porCaminho('solicitacoes-parecer'),
        porCaminho('solicitacoes-internacoes'),
        porCaminho('solicitacoes-hemocomponentes'),
      ]),
    },
    { separator: true },
    {
      label: 'INTERNAÇÃO',
      items: compactar([porCaminho('mapa-cirurgico'), porCaminho('salas-cirurgicas')]),
    },
    { separator: true },
    {
      label: 'CONFIGURAÇÃO',
      items: compactar([porLabel('Configurações Visitas'), porLabel('Configuração')]),
    },
  ];
}

function badgePositivo(valor: string | undefined): string | undefined {
  if (valor == null) return undefined;
  const num = Number(valor);
  return num > 0 ? String(num) : undefined;
}

function construirMenuProntuario(
  entradaId: number,
  quantidades: Partial<QuantidadesMenu>,
  personalizados: string[],
  authService: AuthService,
  router: Router,
): NavegacaoMenuItem[] {
  const itensProntuario = MENU_PRONTUARIO_BASE.filter((item) => !item.showBackButton).map((item): NavegacaoMenuItem => {
    const semPermissao = item.role ? !authService.hasRole(item.role) : false;
    const ehAlergico = item.personalizadoKey && personalizados.includes(item.personalizadoKey) ? 'alergico' : undefined;

    return {
      label: item.label,
      icon: item.icon,
      routerLink: [item.path ? `prontuario/${entradaId}/${item.path}` : `prontuario/${entradaId}`],
      routerLinkActiveOptions: { exact: true },
      disabled: semPermissao,
      tooltip: semPermissao ? 'Sem permissão para acessar este menu' : undefined,
      badge: item.badgeKey ? badgePositivo(quantidades[item.badgeKey]) : undefined,
      class: ehAlergico,
      visible: true,
    };
  });

  return [
    {
      label: 'PRONTUÁRIO',
      items: itensProntuario,
    },
    { separator: true },
    {
      label: 'NAVEGAÇÃO',
      items: [
        {
          label: 'Voltar',
          icon: 'pi pi-fw pi-arrow-left',
          command: (): void => {
            localStorage.setItem('personalizados', '');
            localStorage.removeItem('pep-quantidades-menu');
            void router.navigate(['/pacientes']);
          },
        },
      ],
    },
  ];
}

export const NavegacaoStore = signalStore(
  { providedIn: 'root' },
  withState(estadoInicial),
  withProps(() => ({
    _prontuarioService: inject(ProntuarioService),
    _authService: inject(AuthService),
    _router: inject(Router),
  })),
  withComputed(({ contexto, entradaId, quantidades, personalizados, _authService, _router, termoBuscaMenu }) => ({
    emProntuario: computed(() => contexto() === 'prontuario'),
    itensMenu: computed((): NavegacaoMenuItem[] => {
      const idAtual = entradaId();
      const base =
        contexto() === 'prontuario' && idAtual !== null
          ? construirMenuProntuario(idAtual, quantidades(), personalizados(), _authService, _router)
          : construirMenuInicial(_authService);
      const termo = normalizarTextoBusca(termoBuscaMenu());
      return termo ? filtrarMenuPorTermo(base, termo) : base;
    }),
  })),
  withMethods((store) => ({
    definirTermoBuscaMenu(termo: string): void {
      patchState(store, { termoBuscaMenu: termo });
    },

    atualizarQuantidade(
      propriedade: keyof QuantidadesMenu,
      operador: 'somar' | 'subtrair' | 'valor',
      valor?: number,
    ): void {
      const quantidadesAtuais = store.quantidades();
      let quantidade = 0;

      switch (operador) {
        case 'somar':
          quantidade = Number(quantidadesAtuais[propriedade] ?? 0) + 1;
          break;
        case 'subtrair':
          quantidade = Number(quantidadesAtuais[propriedade] ?? 0) - 1;
          break;
        case 'valor':
          quantidade = valor ?? 0;
          break;
      }

      const novasQuantidades = { ...quantidadesAtuais, [propriedade]: String(quantidade) };
      patchState(store, { quantidades: novasQuantidades });
      localStorage.setItem('pep-quantidades-menu', JSON.stringify(novasQuantidades));
    },

    _sincronizarComRota: rxMethod<string>(
      pipe(
        map((url) => {
          const correspondencia = /^\/prontuario\/(\d+)/.exec(url);
          return {
            entradaId: correspondencia ? Number(correspondencia[1]) : null,
          };
        }),
        distinctUntilChanged((anterior, atual) => anterior.entradaId === atual.entradaId),
        switchMap(({ entradaId }) => {
          if (entradaId === null) {
            if (store.contexto() !== 'inicial') {
              patchState(store, {
                contexto: 'inicial',
                entradaId: null,
                entrada: null,
                quantidades: {},
                personalizados: [],
                termoBuscaMenu: '',
              });
            }
            return EMPTY;
          }

          if (store.entradaId() === entradaId && store.entrada() !== null) {
            return EMPTY;
          }

          const personalizados = localStorage.getItem('personalizados')?.split(',').filter(Boolean) ?? [];
          patchState(store, {
            contexto: 'prontuario',
            entradaId,
            quantidades: {},
            carregandoEntrada: true,
            personalizados,
            termoBuscaMenu: '',
          });

          return store._prontuarioService.obterProntuario(entradaId).pipe(
            tapResponse({
              next: (entrada) => {
                const quantidades: Partial<QuantidadesMenu> = {
                  qtdAlergias: entrada.qtdAlergias,
                  qtdAnexos: entrada.qtdAnexos,
                  qtdAtendimentoMedico: entrada.qtdAtendimentoMedico,
                  qtdAtestado: entrada.qtdAtestado,
                  qtdAutorizacaoInternacao: entrada.qtdAutorizacaoInternacao,
                  qtdBoletimMedico: entrada.qtdBoletimMedico,
                  qtdBoletinsCirurgicos: entrada.qtdBoletinsCirurgicos,
                  qtdDiagnosticoPaciente: String(entrada.qtdDiagnosticoPaciente),
                  qtdEvolucaoMedica: entrada.qtdEvolucaoMedica,
                  qtdParecerMedico: entrada.qtdParecerMedico,
                  qtdPrescricaoMedica: entrada.qtdPrescricaoMedica,
                  qtdPrescricoesAntibiotico: entrada.qtdPrescricoesAntibiotico,
                  qtdPrescricaoQuimio: entrada.qtdPrescricaoQuimio,
                  qtdReceituario: entrada.qtdReceituario,
                  qtdSolicitacaoExame: entrada.qtdSolicitacaoExame,
                  qtdClassificacaoDeRisco: entrada.qtdClassificacaoDeRisco,
                  qtdAcompanhamentoPrescricao: entrada.qtdAcompanhamentoPrescricao,
                  qtdMapasCirurgicos: entrada.qtdMapasCirurgicos,
                };
                localStorage.setItem('pep-quantidades-menu', JSON.stringify(quantidades));
                patchState(store, { entrada, quantidades, carregandoEntrada: false });
              },
              error: () => patchState(store, { carregandoEntrada: false, entrada: null }),
            }),
            finalize(() => {
              if (store.carregandoEntrada() && store.entrada() === null) {
                patchState(store, { carregandoEntrada: false });
              }
            }),
          );
        }),
      ),
    ),
  })),
  withHooks((store) => {
    const router = inject(Router);
    return {
      onInit(): void {
        store._sincronizarComRota(
          router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map((e) => e.urlAfterRedirects),
            startWith(router.url),
          ),
        );
      },
    };
  }),
);
